import React, { FC, useEffect } from 'react'
import { Link, Outlet } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ContentBody } from 'components/pages/components/ContentBody'
import PaperWrapper from 'components/custom/PaperWrapper'
import TitleWrapper from 'components/custom/TitleWrapper'
import LinkCardWrapper from 'components/custom/LinkCardWrapper'

import {
  eLinkTarget,
  eTypographyVariant,
  iBlogPostCard,
} from 'components/types'

import { componentPermissions } from 'config/permissions'
import { componentNames } from 'config/roles'
import { RoutePaths } from 'routes/paths'
import { clearHubCategories } from 'store/slices/dataHubCategories'

import './style.css'

import { ReactComponent as DataHubIcon } from 'assets/icons/032-searching-data-in-database.svg'
import { ReactComponent as ToolboxIcon } from 'assets/icons/017-arrows.svg'
import { ReactComponent as KnowledgeHubIcon } from 'assets/icons/002-idea.svg'
import { ReactComponent as ProfileIcon } from 'assets/icons/profile.svg'
import { ReactComponent as OrgIcon } from 'assets/icons/organization.svg'
import { ReactComponent as BillingIcon } from 'assets/icons/wallet.svg'

type Tile = {
  key: string
  to?: string
  href?: string
  title: string
  subtitle?: string
  blurb?: string // 👈 added
  icon: JSX.Element
}
const Home: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  useEffect(() => {
    return () => {
      dispatch(clearHubCategories())
    }
  }, [dispatch])

  // --- Section 1: Reference data & PCF Calculation --------------------------
  const referenceTiles: Tile[] = [
    {
      key: 'data-hub',
      to: '/data-hub',
      title: 'Data Hub',
      subtitle: 'Reference data & datasets',
      blurb:
        'Central place to access, manage, and analyze sustamize CO2e reference datasets.',
      icon: <DataHubIcon className="w-8 h-8 text-primary-600" />,
    },
    {
      key: 'toolbox',
      to: '/toolbox',
      title: 'Toolbox',
      subtitle: 'PCF generation tools',
      blurb: 'Powerful set of tools for streamlined PCF calculations.',
      icon: <ToolboxIcon className="w-8 h-8 text-primary-600" />,
    },
    {
      key: 'knowledge-hub',
      href: 'https://docs.sustamizer.com',
      title: 'Knowledge Hub',
      subtitle: 'Guides & documentation',
      blurb:
        'Comprehensive documentation, best practices, and how-to guides for PCF workflows.',
      icon: <KnowledgeHubIcon className="w-8 h-8 text-primary-600" />,
    },
  ]

  const profileTiles: Tile[] = [
    {
      key: 'settings',
      to: '/user/settings',
      title: 'My Settings',
      subtitle: 'Preferences & account',
      blurb: 'Manage your profile, language, and system preferences.',
      icon: <ProfileIcon className="w-8 h-8 text-primary-600" />,
    },
    {
      key: 'org',
      to: '/user/organization-profile',
      title: 'My Organization',
      subtitle: 'Company profile & members',
      blurb: 'Configure your organization, add users, and manage roles.',
      icon: <OrgIcon className="w-8 h-8 text-primary-600" />,
    },
    {
      key: 'billing',
      to: '/user/billing',
      title: 'Billing',
      subtitle: 'Plans & payment',
      blurb: 'Explore sustamize offerings to find the most suitable for you.',
      icon: <BillingIcon className="w-8 h-8 text-primary-600" />,
    },
  ]

  // --- Section 3: News & Insights (unchanged logic) -------------------------
  const relativePathToBlogImage = 'assets/images/index'
  const blogPosts: iBlogPostCard[] = [
    {
      title:
        'Recycling Quotas, Secondary Material Quotas... What Is the Right Terminology?',
      imageSrc: `${relativePathToBlogImage}/6475d8927e884ecc0d2d7137_Scrap-p-2600.jpeg`,
      to: 'https://www.sustamize.com/blog/recycling-quotas-secondary-material-quotas-what-is-the-right-terminology',
    },
    {
      title: 'Product Carbon Footprint Calculation Bottom-Up',
      imageSrc: `${relativePathToBlogImage}/627a28493346e0c3d4172f77_AdobeStock_290098754-p-2000-600px.png`,
      to: 'https://www.sustamize.com/blog/pcf-calculation-bottom-up',
    },
    {
      title: '5 reasons why producing companies should measure their PCF',
      imageSrc: `${relativePathToBlogImage}/62b46d91adfdfb7bbd6cc7d1_5 reasons Bild JPG-p-2000-600px.png`,
      to: 'https://www.sustamize.com/blog/5-reasons-why-producing-companies-should-measure-their-pcf',
    },
    {
      title: 'CO2 pricing: a measure to reduce emissions',
      imageSrc: `${relativePathToBlogImage}/629a0f4849b83092e72891db_CO2 price-p-2000-600px.png`,
      to: 'https://www.sustamize.com/blog/co2-pricing',
    },
    {
      title: 'Company-relevant footprints overview',
      imageSrc: `${relativePathToBlogImage}/62aae8f9e473148f44cc9cbd_Fußabdrücke Bild-p-2000-600px.png`,
      to: 'https://www.sustamize.com/blog/company-relevant-footprints-overview',
    },
    {
      title: 'Decarbonizing concrete: a pressing issue',
      imageSrc: `${relativePathToBlogImage}/62b98799a83391f592255dfd_[blog] concrete-p-2000-600px.png`,
      to: 'https://www.sustamize.com/blog/decarbonizing-concrete-a-pressing-issue',
    },
    {
      title:
        'A Meaningful Approach to CO2 Reporting for Manufacturing Companies',
      imageSrc: `${relativePathToBlogImage}/62cc2493827b6564d498c17c_62208a5bd501096a31c0fbe8_[Blog] Sinnvolles Vorgehen beim CO2-Reporting-600px.png`,
      to: 'https://www.sustamize.com/blog/a-meaningful-approach-to-co2-reporting-for-manufacturing-companies',
    },
    {
      title: 'Does “Science-Based” Always Mean Accurate?',
      imageSrc: `${relativePathToBlogImage}/632c30fa79fb9d74481777b7_Science-p-2000-600px.png`,
      to: 'https://www.sustamize.com/blog/does-science-based-always-mean-accurate',
    },
    {
      title: 'Product Carbon Footprint Is Not Only About Carbon Dioxide (CO2)',
      imageSrc: `${relativePathToBlogImage}/6316fc66f7d7f612de2e648b_Question-p-2000-600px.png`,
      to: 'https://www.sustamize.com/blog/product-carbon-footprint-is-not-only-about-carbon-dioxide-co2',
    },
    {
      title:
        'Study: How Do German Manufacturers React to the Increasing Societal Pressure for Decarbonisation?',
      imageSrc: `${relativePathToBlogImage}/63636d66d4d4dde9641b0aa9_Manufacture 1-p-2000-600px.png`,
      to: 'https://www.sustamize.com/blog/study-how-do-german-manufacturers-react-to-the-increasing-societal-pressure-for-decarbonisation',
    },
    {
      title: '6 Ways AI Can Help Reduce Carbon Emissions',
      imageSrc: `${relativePathToBlogImage}/6481664d8e41f40e20c81d6d_Cover AI pic-p-2600.jpeg`,
      to: 'https://www.sustamize.com/blog/6-ways-ai-can-help-reduce-carbon-emissions',
    },
    {
      title:
        'Animal, Vegan and Plant-Based Leather: What Is Truly More Climate-Friendly?',
      imageSrc: `${relativePathToBlogImage}/6454b0c72c310581ebd315c8_Leathers-p-2600.jpeg`,
      to: 'https://www.sustamize.com/blog/animal-vegan-and-plant-based-leather-what-is-truly-more-climate-friendly',
    },
  ]
  const TileCard: FC<{ tile: Tile }> = ({ tile }) => {
    const CardInner = (
      <article
        className="
        h-full w-full
        rounded-2xl border border-gray-200 bg-white
        p-8
        shadow-md
        transition duration-200 ease-out
        hover:-translate-y-1 hover:scale-[1.03] hover:shadow-xl
        focus-visible:-translate-y-1 focus-visible:scale-[1.03] focus-visible:shadow-xl
        flex flex-col
      "
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0">{tile.icon}</div>
          <div>
            <h3 className="text-xl font-semibold leading-tight">
              {tile.title}
            </h3>
            {tile.subtitle && (
              <p className="text-base text-gray-600">{tile.subtitle}</p>
            )}
          </div>
        </div>

        {tile.blurb && (
          <p className="mt-4 text-base text-gray-700 flex-1">{tile.blurb}</p>
        )}

        <div className="mt-6 inline-flex items-center gap-2 text-base font-medium text-primary-700">
          <span className="transition-transform group-hover:translate-x-0.5">
            Open
          </span>
          <svg
            className="h-5 w-5 transition-transform group-hover:translate-x-0.5"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 01-1.414 1.414L12 6.414V16a1 1 0 11-2 0V6.414L6.707 9.707A1 1 0 015.293 8.293l5-5z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </article>
    )

    return tile.to ? (
      <Link to={tile.to} className="group flex-1 block focus:outline-none">
        {CardInner}
      </Link>
    ) : (
      <a
        href={tile.href}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex-1 block focus:outline-none"
      >
        {CardInner}
      </a>
    )
  }
  return (
    <>
      <ContentBody aria-label={t('index.title')} title={t('index.title')}>
        {/* --- Section 1: Reference data & PCF Calculation --- */}
        <PaperWrapper className="p-4 mb-6">
          <TitleWrapper
            variant={eTypographyVariant.h5}
            ariaLabel="Reference data & PCF Calculation"
            className="pb-4 pt-2 pl-8"
          >
            Reference data &amp; PCF Calculation
          </TitleWrapper>

          <div className="row w-full flex flex-wrap px-4 pb-3">
            {referenceTiles.map((tile) => (
              <div key={tile.key} className="px-4 pb-6 w-full md:w-1/3 flex">
                <TileCard tile={tile} />
              </div>
            ))}
          </div>
        </PaperWrapper>

        {/* --- Section 2: User Profile --- */}
        <PaperWrapper className="p-4 mb-6">
          <TitleWrapper
            variant={eTypographyVariant.h5}
            ariaLabel="User Profile"
            className="pb-4 pt-2 pl-8"
          >
            User Profile
          </TitleWrapper>

          <div className="row w-full flex flex-wrap px-4 pb-3">
            {profileTiles.map((tile) => (
              <div key={tile.key} className="px-4 pb-6 w-full md:w-1/3 flex">
                <TileCard tile={tile} />
              </div>
            ))}
          </div>
        </PaperWrapper>

        {/* --- Section 3: News & Insights (unchanged) --- */}
        <PaperWrapper className="p-4">
          <TitleWrapper
            variant={eTypographyVariant.h5}
            ariaLabel={t('index.news_insights')}
            className="pb-4 pt-2 pl-8"
          >
            {t('index.news_insights')}
          </TitleWrapper>

          <div className="row w-full justify-start max-[1100px]:justify-center px-4 pb-3">
            {blogPosts.map((post, index) => (
              <LinkCardWrapper
                blogPost={post}
                target={eLinkTarget.blank}
                className="flex"
                key={`blogPost-${index}-${post.to}`}
              />
            ))}
          </div>
        </PaperWrapper>
      </ContentBody>
      <Outlet />
    </>
  )
}

const exportObj = {
  routeProps: {
    path: RoutePaths.dashboard,
    title: 'pages.home',
    element: <Home />,
    roles: componentPermissions[componentNames.pageDashboard],
    requiresAuthentication: true,
  },
  name: 'Home',
}
export default exportObj
